import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiPost } from '../services/api';
import { useSelectedCity } from '../contexts/SelectedCityContext';
import { getNavSections, markActiveItem } from '../config/navigationConfig';
import { getInitials, getBadgeClass, getSidebarStyles, getSidebarClasses } from '../utils/sidebarUtils';

/**
 * Texto que reduz automaticamente a fonte para caber por completo na
 * largura do container, sem quebrar linha. Usado no nome da cidade do
 * sidebar para que nomes longos (ex.: "São Miguel da Lagoa Tapada - PB")
 * apareçam inteiros sem alterar a largura do sidebar.
 */
function AutoFitText({ text, maxFontSize = 15, minFontSize = 9, className = '', style = {} }) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [fontSize, setFontSize] = useState(maxFontSize);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const textEl = textRef.current;
    if (!container || !textEl) return;

    let size = maxFontSize;
    textEl.style.fontSize = `${size}px`;
    // Reduz a fonte até o texto caber na largura disponível.
    while (textEl.scrollWidth > container.clientWidth && size > minFontSize) {
      size -= 0.5;
      textEl.style.fontSize = `${size}px`;
    }
    setFontSize(size);
  }, [text, maxFontSize, minFontSize]);

  return (
    <div ref={containerRef} className={className} style={{ width: '100%', overflow: 'hidden' }}>
      <span
        ref={textRef}
        style={{ ...style, fontSize: `${fontSize}px`, whiteSpace: 'nowrap', display: 'inline-block' }}
      >
        {text}
      </span>
    </div>
  );
}

/**
 * Dropdown com busca de cidades (apenas admin).
 */
function CityCombobox({ darkMode, cities, selectedCityId, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const selectedCity = useMemo(
    () => cities.find((c) => Number(c.id) === Number(selectedCityId)),
    [cities, selectedCityId]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cities;
    return cities.filter((c) => (c.city || '').toLowerCase().includes(q));
  }, [cities, query]);

  const buttonBase = darkMode
    ? 'bg-slate-800/60 border-slate-700 text-slate-200 hover:bg-slate-800'
    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50';

  const panelBase = darkMode
    ? 'bg-slate-800 border-slate-700 text-slate-200'
    : 'bg-white border-slate-200 text-slate-700';

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border text-sm transition-colors ${buttonBase}`}
        title={selectedCity?.city || 'Selecionar cidade'}
      >
        <span className="flex items-center gap-2 min-w-0">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="truncate font-medium">
            {loading ? 'Carregando...' : (selectedCity?.city || 'Todas as cidades')}
          </span>
        </span>
        <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className={`absolute left-0 right-0 mt-1 rounded-xl border shadow-lg z-50 ${panelBase}`}>
          <div className="p-2 border-b border-inherit">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar cidade..."
              className={`w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-[#0974BA]/40 ${darkMode
                ? 'bg-slate-900 border-slate-700 text-slate-200 placeholder-slate-500'
                : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400'
                }`}
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => { onChange(null); setOpen(false); setQuery(''); }}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${!selectedCityId
                ? (darkMode ? 'bg-[#0974BA]/20 text-white' : 'bg-[#0974BA]/10 text-[#0974BA]')
                : (darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100')
                }`}
            >
              Todas as cidades
            </button>
            {filtered.length === 0 ? (
              <div className={`px-3 py-3 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Nenhuma cidade encontrada
              </div>
            ) : (
              filtered.map((c) => {
                const isSel = Number(c.id) === Number(selectedCityId);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { onChange(c.id); setOpen(false); setQuery(''); }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${isSel
                      ? (darkMode ? 'bg-[#0974BA]/20 text-white' : 'bg-[#0974BA]/10 text-[#0974BA]')
                      : (darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100')
                      }`}
                  >
                    {c.city}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Componente Sidebar reutilizável para todas as páginas do SIDEB
 *
 * @param {Object} props
 * @param {boolean} props.darkMode - Estado do modo escuro
 * @param {boolean} props.collapsed - Se o sidebar está colapsado
 * @param {Function} props.onToggle - Função para alternar o estado de colapso
 */
function Sidebar({ darkMode, collapsed, onToggle, onAbrirGuia, mobileOpen = false, onFecharMobile }) {
  const { user, logout } = useAuth();

  // Troca de acesso: a pessoa pula para outra conta dela sem sair e entrar.
  // Não pede senha porque os acessos de uma pessoa compartilham a mesma — o
  // servidor confere que o destino é dela pelo CPF.
  const [mostrarAcessos, setMostrarAcessos] = useState(false);
  const [trocando, setTrocando] = useState(null);
  const [erroTroca, setErroTroca] = useState('');

  const trocarPara = async (conta) => {
    setTrocando(conta.id);
    setErroTroca('');
    try {
      await apiPost('/users/trocar-acesso/', { id: conta.id });
      // Recarrega do zero: cada perfil abre um menu e um escopo de dados
      // diferentes, e meia troca seria pior que nenhuma.
      window.location.href = '/dashboard';
    } catch (e) {
      setErroTroca(e?.response?.error || 'Não foi possível trocar de acesso.');
      setTrocando(null);
    }
  };
  const location = useLocation();
  const {
    cities,
    selectedCityId,
    setSelectedCityId,
    loading: citiesLoading,
    isAdmin,
  } = useSelectedCity();

  // Obtém as seções de navegação filtradas por role e marca o item ativo
  const navSections = markActiveItem(getNavSections(user), location.pathname);

  const selectedCity = cities.find((c) => Number(c.id) === Number(selectedCityId));

  // Nome da cidade em destaque (ex.: "Santa Rita - PB") para o usuário
  // saber onde está trabalhando. Admin sem cidade selecionada não exibe.
  const cityLabel = selectedCity?.city
    ? `${selectedCity.city}${selectedCity.state ? ` - ${selectedCity.state}` : ''}`
    : null;

  // No celular o menu é uma gaveta por cima da página: navegar tem de
  // fechá-la, senão a pessoa toca num item e continua olhando o menu.
  useEffect(() => {
    if (mobileOpen && onFecharMobile) onFecharMobile();
    // Só a mudança de rota fecha — incluir `mobileOpen` aqui faria a gaveta
    // se fechar no próprio gesto que a abriu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <aside
      className={getSidebarClasses(darkMode, collapsed, mobileOpen)}
      style={getSidebarStyles(darkMode)}
      aria-hidden={undefined}
    >
      {/* Fechar a gaveta — só no celular, onde ela existe. */}
      {onFecharMobile && (
        <button
          type="button"
          onClick={onFecharMobile}
          aria-label="Fechar o menu"
          className={`md:hidden absolute top-4 right-4 p-2 rounded-lg ${darkMode
            ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
            : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      {/* Header com Logo, Cidade e Botão de Toggle */}
      <div className="mb-8">
        <div className={`flex items-center gap-2 ${collapsed ? 'justify-center' : 'justify-between'}`}>
          <div className={`flex items-center min-w-0 ${collapsed ? 'justify-center' : 'gap-3'}`}>
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${darkMode
                ? 'bg-white/10'
                : 'bg-slate-100'
                }`}
            >
              <img
                src="/iconSIDEB.svg"
                alt="SIDEB Logo"
                className="w-8 h-8 object-contain"
              />
            </div>
            {!collapsed && (
              <img src="/LogoNome.svg" alt="SIDEB" className="h-8 w-auto max-w-full object-contain min-w-0" />
            )}
          </div>

          {/* Botão de Toggle - só mostra quando expandido.
              Recebe fundo/borda próprios para ficar visível e destacado do
              nome (flex-shrink-0 impede que a logo o sobreponha). */}
          {!collapsed && (
            <button
              onClick={onToggle}
              aria-label="Minimizar barra lateral"
              className={`hidden md:flex flex-shrink-0 p-2 rounded-lg border transition-colors ${darkMode
                ? 'bg-slate-800/70 border-slate-600/50 text-slate-300 hover:bg-slate-700 hover:text-white'
                : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200 hover:text-slate-800'
                }`}
              title="Minimizar barra lateral"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          )}
        </div>

        {/* Nome da cidade em destaque (onde o usuário está trabalhando) */}
        {!collapsed && cityLabel && (
          <div className="mt-3 flex items-center gap-1.5 px-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 flex-shrink-0 text-[#0974BA]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <AutoFitText
              text={cityLabel}
              maxFontSize={15}
              minFontSize={9}
              className="flex-1 min-w-0"
              style={{ fontWeight: 700, color: '#0974BA', letterSpacing: '0.01em' }}
            />
          </div>
        )}
      </div>

      {/* Botão de Expandir - só mostra quando colapsado */}
      {collapsed && (
        <button
          onClick={onToggle}
          aria-label="Expandir barra lateral"
          className={`w-full p-2 rounded-lg mb-4 border transition-colors ${darkMode
            ? 'bg-slate-800/70 border-slate-600/50 text-slate-300 hover:bg-slate-700 hover:text-white'
            : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200 hover:text-slate-800'
            }`}
          title="Expandir barra lateral"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Seletor de Cidade (somente Admin) */}
      {isAdmin && !collapsed && (
        <div className="mb-6">
          <div
            className={`text-[11px] font-semibold uppercase tracking-wider px-1 mb-2 ${darkMode ? 'text-slate-600' : 'text-slate-400'
              }`}
          >
            Cidade
          </div>
          <CityCombobox
            darkMode={darkMode}
            cities={cities}
            selectedCityId={selectedCityId}
            onChange={setSelectedCityId}
            loading={citiesLoading}
          />
        </div>
      )}

      {isAdmin && collapsed && (
        <div className="mb-4 flex justify-center group relative">
          <button
            onClick={onToggle}
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${darkMode
              ? 'bg-slate-800/60 text-slate-300 hover:bg-slate-800'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            title={selectedCity?.city || 'Selecionar cidade (expanda o menu)'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
          <div className={`absolute left-full ml-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 ${darkMode
            ? 'bg-slate-800 text-slate-200 shadow-lg'
            : 'bg-white text-slate-700 shadow-lg border border-slate-200'
            }`}>
            {selectedCity?.city || 'Todas as cidades'}
          </div>
        </div>
      )}

      {/* Navegação */}
      <nav className="flex-1">
        {navSections.map((section, idx) => (
          <div key={idx} className="mb-6">
            {!collapsed && (
              <div
                className={`text-[11px] font-semibold uppercase tracking-wider px-3 mb-2 ${darkMode ? 'text-slate-600' : 'text-slate-400'
                  }`}
              >
                {section.title}
              </div>
            )}
            {section.items.map((item, itemIdx) => (
              <Link
                key={itemIdx}
                to={item.href}
                className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-3 rounded-xl text-sm font-medium mb-1 transition-all duration-300 group relative ${item.active
                  ? 'text-white'
                  : darkMode
                    ? 'text-slate-400 hover:bg-sideb-blue/10 hover:text-slate-200'
                    : 'text-slate-500 hover:bg-[#0974BA]/10 hover:text-slate-700'
                  }`}
                style={item.active ? { background: 'linear-gradient(135deg, #0974BA, #2E4974)' } : {}}
                title={collapsed ? item.label : undefined}
              >
                <span className={`text-lg ${collapsed ? '' : 'w-6'} text-center flex-shrink-0`}>{item.icon}</span>
                {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}

                {/* Tooltip quando colapsado */}
                {collapsed && (
                  <div className={`absolute left-full ml-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 ${darkMode
                    ? 'bg-slate-800 text-slate-200 shadow-lg'
                    : 'bg-white text-slate-700 shadow-lg border border-slate-200'
                    }`}>
                    {item.label}
                  </div>
                )}
              </Link>
            ))}
          </div>
        ))}
      </nav>

      {/* Footer do Sidebar - Perfil do Usuário */}
      <div className={`pt-4 border-t ${darkMode ? 'border-slate-700/10' : 'border-slate-200'}`}>
        <Link
          to="/perfil"
          className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} mb-3 p-1 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700/40' : 'hover:bg-slate-100'}`}
          title={collapsed ? 'Meu Perfil' : 'Abrir meu perfil'}
        >
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center font-semibold text-white flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #0974BA, #F9B047)' }}
          >
            {user ? getInitials(user.username || user.first_name || user.email) : '??'}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-semibold truncate ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                {user?.username || user?.first_name || user?.email || 'Usuário'}
              </div>
              <span
                className={`inline-block px-2 py-1 rounded-md text-[10px] font-semibold uppercase ${getBadgeClass(user?.role, darkMode)}`}
              >
                {user?.role || 'gestor'}
              </span>
            </div>
          )}
        </Link>

        {/* Trocar de acesso — quem acumula funções tem uma conta por função, e
          sair e entrar de novo só para ver a outra tela é caro no meio do dia */}
      {(user?.outros_acessos || []).length > 0 && !collapsed && (
          <div className="mb-2">
            <button
              type="button"
              onClick={() => setMostrarAcessos((v) => !v)}
              className={`w-full flex items-center justify-between gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${darkMode
                ? 'bg-slate-700/40 text-slate-200 border border-slate-600/40 hover:bg-slate-700/70'
                : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
                }`}
            >
              <span className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                Trocar de acesso
              </span>
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 transition-transform ${mostrarAcessos ? 'rotate-180' : ''}`}
                   fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {mostrarAcessos && (
              <div className="mt-1.5 space-y-1">
                {(user.outros_acessos || []).map((conta) => (
                  <button
                    key={conta.id}
                    type="button"
                    disabled={trocando !== null}
                    onClick={() => trocarPara(conta)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors disabled:opacity-50 ${darkMode
                      ? 'bg-slate-800/60 hover:bg-slate-700/70 text-slate-200'
                      : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'
                      }`}
                  >
                    <div className="font-medium">
                      {trocando === conta.id ? 'Entrando...' : (conta.role_display || conta.role)}
                    </div>
                    <div className={`text-xs truncate ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      @{conta.username}
                    </div>
                  </button>
                ))}
                {erroTroca && (
                  <p className="text-[#E83A3C] text-xs px-1">{erroTroca}</p>
                )}
              </div>
            )}
          </div>
        )}

      {/* Guia de uso — também no botão "?" fixo, mas quem procura ajuda
          costuma procurar no menu primeiro. */}
        {onAbrirGuia && (
          <button
            type="button"
            onClick={onAbrirGuia}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium mb-2 transition-all duration-300 ${darkMode
              ? 'bg-[#0974BA]/15 text-[#4BA3D8] border border-[#0974BA]/25 hover:bg-[#0974BA]/25'
              : 'bg-[#0974BA]/10 text-[#0974BA] border border-[#0974BA]/20 hover:bg-[#0974BA]/20'
              }`}
            title={collapsed ? 'Guia rápido do SIDEB' : undefined}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {!collapsed && 'Guia rápido'}
          </button>
        )}

      {/* Link para Meu Perfil (também acessível via avatar acima) */}
        <Link
          to="/perfil"
          className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium mb-2 transition-all duration-300 ${darkMode
            ? 'bg-slate-700/40 text-slate-200 border border-slate-600/40 hover:bg-slate-700/70'
            : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
            }`}
          title={collapsed ? 'Meu Perfil' : undefined}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {!collapsed && 'Meu Perfil'}
        </Link>

        {/* Botão de Logout */}
        <button
          onClick={async () => {
            // Precisa esperar: é o servidor que apaga o cookie e invalida o
            // token. Sair sem aguardar deixava a requisição pela metade e a
            // sessão viva — invisível enquanto o token ia no header.
            await logout();
            window.location.href = '/login';
          }}
          className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${darkMode
            ? 'bg-sideb-red/10 text-sideb-red border border-sideb-red/20 hover:bg-sideb-red/20'
            : 'bg-[#E83A3C]/10 text-[#E83A3C] border border-[#E83A3C]/20 hover:bg-[#E83A3C]/20'
            }`}
          title={collapsed ? 'Sair' : undefined}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            />
          </svg>
          {!collapsed && 'Sair'}
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;
